import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { IPlaces, ISearchAvailableTimes } from "../../types/SearchAvalilableTimes";
import { useDoctorsData } from "../doctorsData";
import {
  IScheduleAvailabilityProps,
  IScheduleAvailabilityProvider,
} from "./types";

export const BATCH_WINDOW_DAYS = 7;

const iso = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const ScheduleAvailabilityContext =
  createContext<IScheduleAvailabilityProvider>(
    {} as IScheduleAvailabilityProvider
  );

export const ScheduleAvailabilityProvider = ({
  children,
}: IScheduleAvailabilityProps) => {
  const { doctors } = useDoctorsData();

  const [availability, setAvailability] = useState<Record<string, IPlaces[]>>({});
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchAttempted, setBatchAttempted] = useState(false);
  const [batchEndDate, setBatchEndDate] = useState("");

  const doctorIdsKey = doctors.map((d) => d.id).join(",");

  // novos ids: gate volta a segurar os cards antes de eles montarem
  const [prevIdsKey, setPrevIdsKey] = useState("");
  if (doctorIdsKey !== prevIdsKey) {
    setPrevIdsKey(doctorIdsKey);
    if (doctorIdsKey) setBatchAttempted(false);
  }

  useEffect(() => {
    if (!doctors || doctors.length === 0) {
      setAvailability({});
      setBatchEndDate("");
      return;
    }

    let active = true;

    (async () => {
      try {
        setBatchLoading(true);

        const startDate = iso(new Date());
        const endDate = iso(new Date(Date.now() + BATCH_WINDOW_DAYS * 864e5));

        const { data } = (await api.post(
          "Schedule/SearchAvaliableTimes",
          {
            startDate,
            endDate,
            profissionaisIds: doctors.map((d) => d.id),
            especialidadesIds: [],
            conveniosIds: [],
            keywords: [],
            source: 3,
          },
          { timeout: 8000 }
        )) as { data: ISearchAvailableTimes[] };

        if (!active) return;

        const map: Record<string, IPlaces[]> = {};
        data.forEach((doc) => {
          map[String(doc.doctorId)] = doc.places;
        });

        setAvailability(map);
        setBatchEndDate(endDate);
      } catch {
        // lote falhou: cards caem no fallback individual
        if (active) {
          setAvailability({});
          setBatchEndDate("");
        }
      } finally {
        if (active) {
          setBatchLoading(false);
          setBatchAttempted(true);
        }
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorIdsKey]);

  const getDoctorAvailability = (doctorId: number) =>
    availability[String(doctorId)];

  return (
    <ScheduleAvailabilityContext.Provider
      value={{ getDoctorAvailability, batchLoading, batchAttempted, batchEndDate }}
    >
      {children}
    </ScheduleAvailabilityContext.Provider>
  );
};

export const useScheduleAvailability = () =>
  useContext(ScheduleAvailabilityContext);
